/**
 * 8 个角色定义（内容层）：普攻 shot / 炸弹 bomb / 特殊 special 三件套。
 *
 * 契约（与 game/player.js、scenes/gameScene.js 对接）：
 * - stats：基础性能（高速/低速/磁吸半径/判定半径），Player.reset 时应用。
 * - shot.spawn(shots, player, slow, power, ctx?)：Player.update 每帧调用（按住 Z 连发）。
 *   内部按 shot.interval 限速（以 player.time 为基准，下一次可发时刻记在 player._nextShotAt）。
 *   power 0..96，按 0/24/48/72/96 五档提升弹道数/子机数；slow=true 时弹道集中
 *   （例外：moe 心形弹形固定不随 slow 变化——其中心双发即保底，低速价值在走位精度）。
 *   发弹统一走 PlayerShots.spawn({x,y,vx,vy,damage,r,pierce,homing,color})，
 *   速度单位为 px/帧@60fps（上 = vy 负值），color 为 manifest 弹丸 key。
 * - bomb.activate(ctx) / special.activate(ctx)：由 Player 调用，返回 true = 成功发动。
 *   消耗 state.bombs / 无敌 3s / playSfx('bomb') / 消耗特殊槽 已由 Player 统一处理；
 *   清屏敌弹转道具由本层 bombBase() 统一处理。
 * - 持续性效果通过 ctx.mods 计时器字段表达（gameScene 每帧衰减，字段一览见 gameScene.js）：
 *     rageT        怒怒·狂暴：射速×2 伤害×1.5（本文件 spawn 内读取）
 *     optionBoostT 燃燃·全力应援：子机火力×2
 *     confuseT     惑惑·混乱力场：敌机射速减半（game/modsGate.js 经 fireTimer 全调用点遵守）
 *     jitterT     惑惑·思维混乱：屏上敌弹方向错乱（gameScene 执行）
 *     sleepT       困困·梦境领域：敌机睡眠不射弹（game/modsGate.js 经 fireTimer 全调用点遵守）
 *     shieldT      泪泪·泪之护盾：10s 内挡一次中弹（gameScene 执行）
 *     dashT        开开心·无敌冲刺：移速↑+接触伤敌（gameScene 执行）
 * - 延时/重复演出通过 ctx.timers（gameScene 驱动的 {t, fn} 列表）实现。
 * node 可直接 import（所有函数仅在被调用时接触 ctx）。
 */
import { TAU, randRange, randInt, rand } from '../engine/math.js';
import { L, tf } from '../i18n.js';

/** 火力档位：0/24/48/72/96 → 0..4（上限 96，见 state.js POWER_MAX） */
function tier(power) {
  return power >= 96 ? 4 : power >= 72 ? 3 : power >= 48 ? 2 : power >= 24 ? 1 : 0;
}

/** 狂暴（rageT）中的伤害倍率 */
function dmgMul(player) {
  return player.mods && player.mods.rageT > 0 ? 1.3 : 1; // 狂暴伤害×1.3
}

/** 构造带限速的普攻定义：interval 秒一发；狂暴时射速×2。 */
function makeShot(interval, fire) {
  return {
    interval,
    spawn(shots, player, slow, power, ctx) {
      if (player.time < (player._nextShotAt || 0)) return;
      const rateMul = player.mods && player.mods.rageT > 0 ? 0.625 : 1; // 狂暴射速×1.6
      player._nextShotAt = player.time + interval * rateMul;
      fire(shots, player, slow, tier(power), power, ctx);
    },
  };
}

function setMod(ctx, key, seconds) {
  if (ctx.mods) ctx.mods[key] = seconds;
}

/** 延时执行（无计时器环境立即执行，保证冒烟测试可观测）。 */
function after(ctx, seconds, fn) {
  if (ctx.timers) ctx.timers.push({ t: seconds, fn });
  else fn();
}

/** 每 interval 秒重复 times 次（首次立即）。 */
function repeat(ctx, times, interval, fn) {
  for (let i = 0; i < times; i++) after(ctx, interval * i, () => fn(i));
}

/** bomb 统一基座：清屏敌弹转道具 + 白闪（耗弹/无敌/音效由 Player 层处理）。 */
function bombBase(ctx) {
  if (ctx.field && ctx.items) ctx.field.clearToItems(ctx.items);
  if (ctx.effects) ctx.effects.flash(0.5);
}

/** 对屏上全部敌人（含 boss）造成伤害。 */
function damageAll(ctx, dmg) {
  if (ctx.enemies) {
    ctx.enemies.forEachActive((e) => ctx.enemies.damage(e, dmg, ctx));
  }
  if (ctx.boss && ctx.boss.alive) ctx.boss.damage(dmg);
}

/** 把最多 limit 颗敌弹按比例 ratio 转化为 POINT 道具，返回转化数。 */
function convertBullets(ctx, limit, ratio) {
  if (!ctx.field || !ctx.items) return 0;
  let n = 0;
  ctx.field.forEachActive((b) => {
    if (n >= limit) return;
    if (rand() < ratio) {
      ctx.items.spawn(b.x, b.y, 'POINT');
      ctx.field.pool.release(b);
      n++;
    }
  });
  return n;
}

export const CHARACTERS = [
  {
    id: 'happy',
    stats: { speedFast: 4.6, speedSlow: 1.8, magnet: 64, hitbox: 2.5 }, // 均衡偏快
    name: 'KaiKai',
    cnName: '开开心',
    sprite: 'happy',
    spriteKey: 'happy',
    color: '#ffd84d',
    desc: '永远元气满满的乐天派，用漫天弹幕把快乐撒向全屏。',
    shot: Object.assign(
      makeShot(0.12, (shots, p, slow, t) => {
        const dmg = 1.9 * dmgMul(p);
        const n = 3 + t; // 火力提升增加弹道：3→7 向
        const arc = slow ? 0.5 : 1.0; // 低速收束
        // 中心 2 发直线机炮：保底单目标输出（散射型的对 boss 下限）
        shots.spawn({ x: p.x - 3, y: p.y - 10, vy: -14, damage: dmg, r: 4, color: 'star_yellow' });
        shots.spawn({ x: p.x + 3, y: p.y - 10, vy: -14, damage: dmg, r: 4, color: 'star_yellow' });
        for (let i = 0; i < n; i++) {
          const a = -Math.PI / 2 + (i / (n - 1) - 0.5) * arc;
          shots.spawn({ x: p.x, y: p.y - 10, vx: Math.cos(a) * 13, vy: Math.sin(a) * 13, damage: dmg, r: 4, color: 'star_yellow' });
        }
      }),
      { name: '开心散射', desc: '扇形散射，火力提升增加弹道（3→7 向），低速收束' },
    ),
    bomb: {
      name: '满天烟花',
      desc: '2s 连环分区爆破：每爆清一区并伤敌（伪全屏）',
      activate(ctx) {
        // 演出驱动：六色烟花分区扫屏——每次只清一个区域（伪全屏），
        // 与怒怒「震爆」的瞬时全屏拉开形态差异（用户定案）。
        // 分区路径 Z 字扫场（注入 rng 抖动，录像确定）
        const ZONES = [
          [120, 160], [360, 200], [240, 330], [120, 470], [360, 500], [240, 240],
        ];
        const HUES = ['#f66', '#fc3', '#6f6', '#6cf', '#c9f', '#f9c'];
        if (ctx.audio) ctx.audio.playSfx('explosion');
        if (ctx.effects) ctx.effects.flash(0.3);
        repeat(ctx, 6, 0.4, (i) => {
          const [zx, zy] = ZONES[i % ZONES.length];
          const cx = zx + randRange(-40, 40);
          const cy = zy + randRange(-30, 30);
          const R = 135;
          const hue = HUES[i % HUES.length];
          // 升空尾迹 → 绽放（先演出后结算，看得见每一次爆破位置）
          if (ctx.effects) {
            for (let k = 0; k < 6; k++) ctx.effects.sparkle(cx + randRange(-8, 8), cy + 60 - k * 14, hue);
            ctx.effects.explosion(cx, cy, 2.2);
            for (let k = 0; k < 14; k++) {
              const a = (k / 14) * TAU;
              ctx.effects.sparkle(cx + Math.cos(a) * R * 0.75, cy + Math.sin(a) * R * 0.75, hue);
            }
          }
          // 区内敌弹转道具 + 区内敌伤（伪全屏：只清这一区）
          if (ctx.field && ctx.items) {
            ctx.field.forEachActive((b) => {
              if (Math.hypot(b.x - cx, b.y - cy) < R) {
                ctx.items.spawn(b.x, b.y, 'POINT');
                ctx.field.pool.release(b);
              }
            });
          }
          if (ctx.enemies) {
            ctx.enemies.forEachActive((e) => {
              if (Math.hypot(e.x - cx, e.y - cy) < R) ctx.enemies.damage(e, 30, ctx);
            });
          }
          if (ctx.boss && ctx.boss.alive && Math.hypot(ctx.boss.x - cx, ctx.boss.y - cy) < R) ctx.boss.damage(30);
        });
        return true;
      },
    },
    special: {
      name: '无敌冲刺',
      desc: '2.5s 无敌 + 移速提升 + 接触伤敌',
      activate(ctx) {
        setMod(ctx, 'dashT', 2.5);
        if (ctx.player) ctx.player.invulnUntil = ctx.player.time + 2.5;
        if (ctx.effects && ctx.player) ctx.effects.floatText(ctx.player.x, ctx.player.y - 20, L('无敌冲刺！'), '#ff4');
        return true;
      },
    },
  },

  {
    id: 'cool',
    stats: { speedFast: 4.8, speedSlow: 1.9, magnet: 56, hitbox: 2.0 }, // 最快+最小判定，磁吸最小
    name: 'KuZong',
    cnName: '酷总',
    sprite: 'cool',
    spriteKey: 'cool',
    color: '#6cd8ff',
    desc: '戴着墨镜的冷酷射手，弹无虚发，人狠话不多。',
    shot: Object.assign(
      makeShot(0.11, (shots, p, slow, t) => {
        const dmg = 3.8 * dmgMul(p); // 贯通按目标去重后抬伤保性格（正下 ~69 DPS）
        const gap = slow ? 2 : 6; // 低速双枪并拢
        shots.spawn({ x: p.x - gap, y: p.y - 10, vy: -18, damage: dmg, r: 3, pierce: 2, color: 'pill_cyan' });
        shots.spawn({ x: p.x + gap, y: p.y - 10, vy: -18, damage: dmg, r: 3, pierce: 2, color: 'pill_cyan' });
        // 火力提升追加斜向副炮：0/1/1/2/2 对
        const pairs = [0, 1, 1, 2, 2][t];
        for (let i = 0; i < pairs; i++) {
          const off = 12 + i * 8;
          shots.spawn({ x: p.x - off, y: p.y - 6, vx: -2 - i, vy: -16, damage: dmg * 0.6, r: 3, pierce: 1, color: 'pill_blue' });
          shots.spawn({ x: p.x + off, y: p.y - 6, vx: 2 + i, vy: -16, damage: dmg * 0.6, r: 3, pierce: 1, color: 'pill_blue' });
        }
      }),
      { name: '酷穿双枪', desc: '窄直线贯通弹，火力提升追加斜向副炮' },
    ),
    bomb: {
      name: '贯穿主炮',
      desc: '以自机为中心的纵激光柱，持续 1.5s',
      activate(ctx) {
        bombBase(ctx);
        const px = ctx.player ? ctx.player.x : 240;
        // 光柱持续伤害（15 跳 × 0.1s）
        repeat(ctx, 15, 0.1, () => {
          if (ctx.enemies) {
            ctx.enemies.forEachActive((e) => {
              if (Math.abs(e.x - px) < 44) ctx.enemies.damage(e, 20, ctx);
            });
          }
          if (ctx.boss && ctx.boss.alive && Math.abs(ctx.boss.x - px) < 60) ctx.boss.damage(20);
          if (ctx.effects) {
            ctx.effects.flash(0.12);
            for (let i = 0; i < 3; i++) ctx.effects.sparkle(px + randRange(-30, 30), randRange(0, 640), '#6cf');
          }
        });
        // 视觉弹体：粗壮贯通柱
        if (ctx.playerShots && ctx.player) {
          for (let i = 0; i < 4; i++) {
            ctx.playerShots.spawn({ x: px, y: ctx.player.y - 20 - i * 90, vy: -30, damage: 10, r: 10, pierce: 999, color: 'pill_cyan' });
          }
        }
        return true;
      },
    },
    special: {
      name: '子弹时间',
      desc: '全场时间 0.35×，持续 2.5s',
      activate(ctx) {
        if (ctx.loop) {
          ctx.loop.timeScale = 0.35;
          after(ctx, 2.5, () => { ctx.loop.timeScale = 1; }); // 场景退出时 gameScene 也会兜底恢复
        }
        return true;
      },
    },
  },

  {
    id: 'angry',
    stats: { speedFast: 4.3, speedSlow: 1.7, magnet: 64, hitbox: 2.8 }, // 偏慢+判定偏大
    name: 'NuNu',
    cnName: '怒怒',
    sprite: 'angry',
    spriteKey: 'angry',
    color: '#ff5a4d',
    desc: '一点就着的暴脾气，火球所到之処寸草不生。',
    shot: Object.assign(
      makeShot(0.16, (shots, p, slow, t) => {
        const base = 4.5 * dmgMul(p); // 贯通按目标去重后抬伤（低速 ~56）
        // 火力提升增加弹道：1→5 向
        const n = 1 + t;
        const angles = slow ? [0] : Array.from({ length: n }, (_, i) => (i - (n - 1) / 2) * 0.18);
        for (const off of angles) {
          const a = -Math.PI / 2 + off;
          shots.spawn({ x: p.x, y: p.y - 10, vx: Math.cos(a) * 12, vy: Math.sin(a) * 12, damage: slow ? base * 2.0 : base, r: 5, pierce: 2, color: 'orb_red' });
        }
      }),
      { name: '怒火三连', desc: '贯通火球，火力提升增加弹道（1→5 向），低速合成重击' },
    ),
    bomb: {
      name: '震爆',
      desc: '全屏伤敌 + 剧烈震屏 + 清弹',
      activate(ctx) {
        bombBase(ctx);
        damageAll(ctx, 60);
        if (ctx.effects) {
          ctx.effects.shake(30, 8);
          ctx.effects.flash(0.7);
        }
        if (ctx.audio) ctx.audio.playSfx('explosion');
        return true;
      },
    },
    special: {
      name: '狂暴',
      desc: '6s 射速×1.6、伤害×1.3',
      activate(ctx) {
        setMod(ctx, 'rageT', 6);
        if (ctx.effects && ctx.player) {
          ctx.effects.floatText(ctx.player.x, ctx.player.y - 20, L('狂暴！！'), '#f66');
          for (let i = 0; i < 10; i++) ctx.effects.sparkle(ctx.player.x + randRange(-24, 24), ctx.player.y + randRange(-24, 24), '#f66');
        }
        return true;
      },
    },
  },

  {
    id: 'cry',
    stats: { speedFast: 4.4, speedSlow: 1.8, magnet: 80, hitbox: 2.5 }, // 磁吸最大
    name: 'LeiLei',
    cnName: '泪泪',
    sprite: 'cry',
    spriteKey: 'cry',
    color: '#7db8ff',
    desc: '爱哭的软妹子，眼泪会自己找到敌人，护盾悄悄守护她。',
    shot: Object.assign(
      makeShot(0.14, (shots, p, slow, t) => {
        const dmg = 2.8 * dmgMul(p); // 追踪必中，3.2 时满火力 DPS 91 超 80 上限，2.8 正好 80
        const n = slow ? 2 : 2 + Math.min(t, 2); // 火力提升增加连发：2→4
        for (let i = 0; i < n; i++) {
          const off = (i - (n - 1) / 2) * 8;
          shots.spawn({ x: p.x + off, y: p.y - 8, vx: off * 0.15, vy: -10, damage: dmg, r: 4, homing: slow ? 1.2 : 0.8, color: 'drop_blue' });
        }
      }),
      { name: '泪之诱导', desc: '自动追踪敌人的泪弹，火力提升增加连发数' },
    ),
    bomb: {
      name: '泪之护盾',
      desc: '清弹 + 10s 内挡下一次中弹',
      activate(ctx) {
        bombBase(ctx);
        setMod(ctx, 'shieldT', 10);
        if (ctx.effects && ctx.player) {
          ctx.effects.floatText(ctx.player.x, ctx.player.y - 20, L('护盾展开'), '#6cf');
          ctx.effects.sparkle(ctx.player.x, ctx.player.y, '#6cf');
        }
        return true;
      },
    },
    special: {
      name: '大哭',
      desc: '全屏敌弹 50% 变成点道具（上限 300 颗）',
      activate(ctx) {
        // 演出驱动：蓝色泪珠下雨，被淋到的弹幕化成道具——
        // 与萌萌「魅惑」拉开形态（经济型 50%/300 vs 无敌型 100%/200+无敌）。
        const n = convertBullets(ctx, 300, 0.5);
        const cryMsg = tf('float.cry', { n });
        if (ctx.effects && ctx.player) {
          ctx.effects.floatText(ctx.player.x, ctx.player.y - 20, cryMsg, '#8cf');
          for (let i = 0; i < 16; i++) ctx.effects.sparkle(randRange(0, 480), randRange(0, 480), '#8cf');
        }
        return true;
      },
    },
  },

  {
    id: 'confused',
    stats: { speedFast: 4.5, speedSlow: 1.8, magnet: 64, hitbox: 2.5 }, // 全均衡
    name: 'HuoHuo',
    cnName: '惑惑',
    sprite: 'confused',
    spriteKey: 'confused',
    color: '#c99df5',
    desc: '满脸问号的迷之存在，弹道和敌人一起陷入混乱。',
    shot: Object.assign(
      makeShot(0.13, (shots, p, slow, t) => {
        const dmg = 2.4 * dmgMul(p);
        const n = randInt(2 + t, 4 + t); // 火力提升增加弹数
        const baseA = -Math.PI / 2 + randRange(-0.35, 0.35) * (slow ? 0.4 : 1);
        // 中心 2 发直线机炮：混乱中的保底单目标输出
        shots.spawn({ x: p.x - 3, y: p.y - 10, vy: -14, damage: dmg, r: 4, color: 'orb_purple' });
        shots.spawn({ x: p.x + 3, y: p.y - 10, vy: -14, damage: dmg, r: 4, color: 'orb_purple' });
        for (let i = 0; i < n; i++) {
          const a = baseA + (i / (n - 1) - 0.5) * (slow ? 0.5 : 0.9);
          shots.spawn({ x: p.x, y: p.y - 10, vx: Math.cos(a) * 12, vy: Math.sin(a) * 12, damage: dmg, r: 4, color: 'orb_purple' });
        }
      }),
      { name: '迷惑散射', desc: '随机角度散射，火力提升增加弹数' },
    ),
    bomb: {
      name: '混乱力场',
      desc: '清弹 + 敌机 5s 内射击错乱（射速减半）',
      activate(ctx) {
        bombBase(ctx);
        setMod(ctx, 'confuseT', 5);
        damageAll(ctx, 10);
        if (ctx.effects) ctx.effects.flash(0.3);
        return true;
      },
    },
    special: {
      name: '思维混乱',
      desc: '4s 内屏上敌弹方向错乱（随机摆动）',
      activate(ctx) {
        // 演出驱动：紫色问号线乱跳，敌弹迷失方向——与酷总「子弹时间」的
        // 全局减速拉开形态（错乱≠变慢）。（用户定案）
        setMod(ctx, 'jitterT', 4);
        if (ctx.effects && ctx.player) {
          ctx.effects.floatText(ctx.player.x, ctx.player.y - 20, L('诶…？'), '#c9f');
          for (let i = 0; i < 8; i++) ctx.effects.sparkle(ctx.player.x + randRange(-30, 30), ctx.player.y + randRange(-30, 30), '#c9f');
        }
        return true;
      },
    },
  },

  {
    id: 'cheer',
    stats: { speedFast: 4.7, speedSlow: 1.9, magnet: 64, hitbox: 2.5 }, // 快速
    name: 'RanRan',
    cnName: '燃燃',
    sprite: 'cheer',
    spriteKey: 'cheer',
    color: '#ffb24d',
    desc: '热血应援团长，环绕子机就是她的啦啦队。',
    shot: Object.assign(
      makeShot(0.14, (shots, p, slow, t) => {
        const dmg = 3.2 * dmgMul(p);
        const gap = slow ? 2 : 4;
        shots.spawn({ x: p.x - gap, y: p.y - 10, vy: -16, damage: dmg, r: 4, color: 'star_orange' });
        shots.spawn({ x: p.x + gap, y: p.y - 10, vy: -16, damage: dmg, r: 4, color: 'star_orange' });
        // 环绕子机（火力提升增加数量：0→4），全力应援中火力×2
        const optN = t;
        const optDmg = (p.mods && p.mods.optionBoostT > 0 ? 2 : 1) * dmgMul(p); // 子机基准伤害 1 为刻意设计（弱子机），special「火力×2」即 1→2
        for (let i = 0; i < optN; i++) {
          const a = p.time * 4 + (i / optN) * TAU;
          shots.spawn({ x: p.x + Math.cos(a) * 28, y: p.y + Math.sin(a) * 28, vy: -13, damage: optDmg, r: 3, color: 'star_yellow' });
        }
      }),
      { name: '应援直线弹', desc: '直线弹 + 环绕子机，火力提升增加子机数' },
    ),
    bomb: {
      name: '应援结界',
      desc: '环波 3 波 + 每波 10 向子机齐射',
      activate(ctx) {
        // 演出驱动：金色环波每波伴随一圈实弹齐射——与萌萌「爱心爆发」的
        // 追踪心弹拉开形态（环波齐射 vs 追踪）。（用户定案）
        bombBase(ctx);
        const px = ctx.player ? ctx.player.x : 240;
        const py = ctx.player ? ctx.player.y : 500;
        repeat(ctx, 3, 0.35, (i) => {
          const radius = 140 + i * 90;
          if (ctx.enemies) {
            ctx.enemies.forEachActive((e) => {
              if (Math.hypot(e.x - px, e.y - py) < radius) ctx.enemies.damage(e, 20, ctx);
            });
          }
          if (ctx.boss && ctx.boss.alive) ctx.boss.damage(20);
          if (ctx.effects) {
            for (let k = 0; k < 12; k++) {
              const a = (k / 12) * TAU;
              ctx.effects.sparkle(px + Math.cos(a) * radius * 0.6, py + Math.sin(a) * radius * 0.6, '#fc3');
            }
          }
          // 子机齐射：10 向实弹（自机弹，真实命中结算）
          if (ctx.playerShots) {
            for (let k = 0; k < 10; k++) {
              const a = (k / 10) * TAU + i * 0.3;
              ctx.playerShots.spawn({ x: px, y: py, vx: Math.cos(a) * 9, vy: Math.sin(a) * 9, damage: 8, r: 4, color: 'star_yellow' });
            }
          }
        });
        return true;
      },
    },
    special: {
      name: '全力应援',
      desc: '8s 子机火力×2 + 自机移速×1.15',
      activate(ctx) {
        setMod(ctx, 'optionBoostT', 8);
        if (ctx.effects && ctx.player) {
          ctx.effects.floatText(ctx.player.x, ctx.player.y - 20, L('加油！加油！'), '#fc3');
          for (let i = 0; i < 10; i++) {
            const a = (i / 10) * TAU;
            ctx.effects.sparkle(ctx.player.x + Math.cos(a) * 36, ctx.player.y + Math.sin(a) * 36, '#fc3');
          }
        }
        return true;
      },
    },
  },

  {
    id: 'sleepy',
    stats: { speedFast: 4.2, speedSlow: 1.6, magnet: 72, hitbox: 3.0 }, // 最慢+判定最大，磁吸补偿
    name: 'KunKun',
    cnName: '困困',
    sprite: 'sleepy',
    spriteKey: 'sleepy',
    color: '#9aa7e8',
    desc: '永远睡不醒的重炮手，醒来的一炮惊天动地。',
    shot: Object.assign(
      makeShot(0.34, (shots, p, slow, t) => {
        const dmg = 12 * dmgMul(p) * (slow ? 1.3 : 1); // 贯通按目标去重后抬伤
        shots.spawn({ x: p.x, y: p.y - 12, vy: -9, damage: dmg, r: 8, pierce: 1, color: 'drop_purple' });
        // 火力提升追加副炮：0/1/1/2/2 门
        const smalls = [0, 1, 1, 2, 2][t];
        for (let i = 0; i < smalls; i++) {
          const side = i % 2 === 0 ? -1 : 1;
          shots.spawn({ x: p.x + side * (12 + Math.floor(i / 2) * 10), y: p.y - 8, vy: -9, damage: dmg * 0.35, r: 5, color: 'drop_purple' });
        }
      }),
      { name: '瞌睡重炮', desc: '大弹高伤炮，火力提升追加副炮，低速伤害再加成' },
    ),
    bomb: {
      name: '梦境领域',
      desc: '大范围清弹 + 敌机睡眠 3s（不射弹）',
      activate(ctx) {
        bombBase(ctx);
        setMod(ctx, 'sleepT', 3);
        damageAll(ctx, 15);
        if (ctx.effects) ctx.effects.flash(0.4);
        return true;
      },
    },
    special: {
      name: '睡遁',
      desc: '0.8s 无敌并向前瞬移一段距离',
      activate(ctx) {
        // 演出驱动：原地淡出（留下 Zzz 残影）+ 前方现身——主动技取代被动的
        // 20s 自动回避（无操作感），保留"睡"的演出。（用户定案）
        if (ctx.player) {
          // 取 max：不得截断已有的更长无敌（如 bomb 3s）；并清已登记中弹——
          // 睡遁是无敌技，登记中的 pendingDeath 不应再结算（审计发现两处）
          ctx.player.invulnUntil = Math.max(ctx.player.invulnUntil, ctx.player.time + 0.8);
          ctx.player._pendingDeath = -1;
          if (ctx.effects) {
            ctx.effects.floatText(ctx.player.x, ctx.player.y - 20, 'Zzz…', '#c9f');
            ctx.effects.sparkle(ctx.player.x, ctx.player.y, '#c9f');
          }
          ctx.player.y = Math.max(60, ctx.player.y - 130);
          if (ctx.effects) {
            ctx.effects.flash(0.25);
            ctx.effects.sparkle(ctx.player.x, ctx.player.y, '#fff');
          }
        }
        return true;
      },
    },
  },

  {
    id: 'moe',
    stats: { speedFast: 4.5, speedSlow: 1.8, magnet: 64, hitbox: 2.2 }, // 判定偏小
    name: 'MengMeng',
    cnName: '萌萌',
    sprite: 'moe',
    spriteKey: 'moe',
    color: '#ff9ad5',
    desc: '卖萌担当，连子弹都是爱你的形状。',
    shot: Object.assign(
      makeShot(0.15, (shots, p, slow, t) => {
        const dmg = 1.6 * dmgMul(p);
        // 中心双发：保底单目标输出（爱心弹为扩散型）
        shots.spawn({ x: p.x - 3, y: p.y - 10, vy: -13, damage: 3.5 * dmgMul(p), r: 4, color: 'orb_pink' });
        shots.spawn({ x: p.x + 3, y: p.y - 10, vy: -13, damage: 3.5 * dmgMul(p), r: 4, color: 'orb_pink' });
        // 沿心形参数曲线取速度方向，左右镜像成对（火力提升增加对数：1→3）
        const pairs = [1, 1, 2, 2, 3][t];
        for (let i = 0; i < pairs; i++) {
          const tt = 0.3 + i * 0.65; // 首对近垂直（对 boss 保底），其余展开成心形
          const hx = 16 * Math.sin(tt) ** 3;
          const hy = 13 * Math.cos(tt) - 5 * Math.cos(2 * tt) - 2 * Math.cos(3 * tt) - Math.cos(4 * tt);
          const l = Math.hypot(hx, hy) || 1;
          const dx = hx / l;
          const dy = -hy / l; // 翻转朝上
          shots.spawn({ x: p.x, y: p.y - 10, vx: dx * 11, vy: dy * 11, damage: dmg, r: 4, color: 'orb_pink' });
          shots.spawn({ x: p.x, y: p.y - 10, vx: -dx * 11, vy: dy * 11, damage: dmg, r: 4, color: 'orb_pink' });
        }
      }),
      { name: '爱心扩散', desc: '心形扩散弹，火力提升增加弹数' },
    ),
    bomb: {
      name: '爱心爆发',
      desc: '2 波心形追踪弹（每波 6 颗）锁定敌人',
      activate(ctx) {
        // 演出驱动：粉色爱心先散开再拐向敌人——与燃燃「应援结界」的
        // 环波齐射拉开形态（追踪 vs 齐射）。
        bombBase(ctx);
        const px = ctx.player ? ctx.player.x : 240;
        const py = ctx.player ? ctx.player.y : 500;
        repeat(ctx, 2, 0.45, (i) => {
          if (ctx.playerShots) {
            for (let k = 0; k < 6; k++) {
              // 初速限制在上半球（-5/6π..-1/6π）：贴底放 bomb 时向下初速会
              // 来不及追踪转向就被出界回收（审计发现）
              const ang = -Math.PI / 2 + (k - 2.5) * 0.7 + i * 0.15;
              ctx.playerShots.spawn({ x: px, y: py, vx: Math.cos(ang) * 5, vy: Math.sin(ang) * 5, damage: 14, r: 5, homing: 1.1, color: 'orb_pink' });
            }
          }
          if (ctx.effects) {
            for (let k = 0; k < 10; k++) {
              const a = (k / 10) * TAU;
              ctx.effects.sparkle(px + Math.cos(a) * 60, py + Math.sin(a) * 60, '#f9c');
            }
          }
        });
        return true;
      },
    },
    special: {
      name: '魅惑',
      desc: '屏上敌弹变点道具（上限 200）+ 3s 无敌',
      activate(ctx) {
        const n = convertBullets(ctx, 200, 1);
        if (ctx.player) ctx.player.invulnUntil = ctx.player.time + 3;
        const charmMsg = tf('float.charm', { n });
        if (ctx.effects && ctx.player) ctx.effects.floatText(ctx.player.x, ctx.player.y - 20, charmMsg, '#f9c');
        return true;
      },
    },
  },
];
